import type { CSSProperties } from "react";
import type { HexVariant } from "@/lib/liquidity-filter";

/** Neon palette shared with liquidity pie / bid-ask charts. */
export const CHART_SERIES_PALETTE = [
  "#00EAFF",
  "#FF0F6F",
  "#8000FF",
  "#03FF82",
  "#FF851F",
  "#FF00FF",
  "#0080FF",
  "#FFDB01",
  "#E619E6",
  "#34ebc9",
] as const;

export const BID_ASK_COLORS = ["#03FF82", "#FF0F6F"] as const;

/** Semantic series colors — keys are normalized (uppercase, alphanumeric). */
const SERIES_COLOR_BY_KEY: Record<string, string> = {
  EHEX: "#00EAFF",
  PHEX: "#FF0F6F",
  EHEXSHARE: "#00EAFF",
  PHEXSHARE: "#FF0F6F",
  BTC: "#FF851F",
  ETH: "#03FF82",
  PLS: "#8000FF",
  PLSX: "#34ebc9",
  INC: "#FF00FF",
  BNB: "#FFDB01",
  SOL: "#0080FF",
  COMBINEDHEX: "#FF0F6F",
  COMBINEDSHARE: "#8000FF",
  ADDRESSES: "#00EAFF",
  MA30: "#FF00FF",
  EHEXCURRENTSTAKERS: "#00EAFF",
  EHEXTOTALHOLDERS: "#0080FF",
  EHEXTOTALSTAKERS: "#34ebc9",
  PHEXCURRENTSTAKERS: "#FF0F6F",
  PHEXTOTALHOLDERS: "#8000FF",
  PHEXTOTALSTAKERS: "#FF851F",
};

export const HEX_VARIANT_PILL: Record<
  HexVariant,
  { className: string; color: string }
> = {
  ehex: {
    color: "#00EAFF",
    className:
      "rounded-md border border-cyan-400/30 bg-cyan-400/10 px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider text-cyan-300",
  },
  phex: {
    color: "#FF0F6F",
    className:
      "rounded-md border border-fuchsia-400/30 bg-fuchsia-400/10 px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider text-fuchsia-300",
  },
};

function normKey(value: string) {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function matchSemanticColor(key: string, title: string): string | null {
  const keyNorm = normKey(key);
  if (SERIES_COLOR_BY_KEY[keyNorm]) return SERIES_COLOR_BY_KEY[keyNorm];

  const titleNorm = normKey(title);
  if (SERIES_COLOR_BY_KEY[titleNorm]) return SERIES_COLOR_BY_KEY[titleNorm];

  if (titleNorm.includes("EHEX")) return SERIES_COLOR_BY_KEY.EHEX;
  if (titleNorm.includes("PHEX")) return SERIES_COLOR_BY_KEY.PHEX;

  return null;
}

export function resolveSeriesColor(
  key: string,
  title: string,
  index = 0,
): string {
  return (
    matchSemanticColor(key, title) ??
    CHART_SERIES_PALETTE[index % CHART_SERIES_PALETTE.length]
  );
}

export function seriesLineStyle(stroke: string): CSSProperties {
  return { filter: `drop-shadow(0 0 5px ${stroke}88)` };
}
